import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function VerifyDomain() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
      return;
    }

    const verifyDomain = async () => {
      try {
        const response = await api.post('/domains/verify', { token });
        setStatus('success');
        setMessage(`Domain ${response.data.domain} has been successfully verified!`);
        setTimeout(() => {
          navigate('/domains');
        }, 3000);
      } catch (error) {
        setStatus('error');
        
        // Handle different types of error responses
        let errorMessage = 'Failed to verify domain. The link may be expired or invalid.';
        
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (Array.isArray(error.response.data)) {
            // Handle validation errors array
            errorMessage = error.response.data.map(err => 
              typeof err === 'string' ? err : err.msg || err.message || 'Validation error'
            ).join(', ');
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setMessage(errorMessage);
      }
    };

    verifyDomain();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-mint-500 to-mint-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">T</span>
            </div>
          </div>
          
          {status === 'verifying' && (
            <>
              <LoadingSpinner size="lg" className="mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Verifying domain ownership...
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Please wait while we verify your domain.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Domain verified!
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {message}
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Redirecting to your domains...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircleIcon className="mx-auto h-16 w-16 text-red-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Verification failed
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {message}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/domains')}
                  className="bg-mint-600 text-white px-6 py-2 rounded-md hover:bg-mint-700 transition-colors"
                >
                  Go to Domains
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
